import { newId } from '../util/id';
import { STICKY_COLORS } from './palette';
import type { BaseObject, BoardObject, ObjectOf, ObjectType, TextStyle } from './types';

export const DEFAULT_TEXT_STYLE: TextStyle = {
  fontSize: 16,
  fontFamily: 'sans',
  color: '#1a1a2e',
  bold: false,
  italic: false,
  underline: false,
  strike: false,
  align: 'center',
  valign: 'middle',
};

export const DEFAULT_SIZES: Record<ObjectType, { w: number; h: number }> = {
  sticky: { w: 200, h: 200 },
  text: { w: 240, h: 40 },
  shape: { w: 200, h: 140 },
  connector: { w: 0, h: 0 },
  drawing: { w: 0, h: 0 },
  image: { w: 320, h: 240 },
  frame: { w: 800, h: 560 },
  card: { w: 280, h: 140 },
  table: { w: 360, h: 120 },
  mindmap: { w: 160, h: 44 },
  emoji: { w: 64, h: 64 },
};

type TypeDefaults = { [T in ObjectType]: Omit<ObjectOf<T>, keyof BaseObject> };

const TYPE_DEFAULTS: TypeDefaults = {
  sticky: { text: '', color: STICKY_COLORS[0], fontSize: null, align: 'center' },
  text: { text: '', style: { ...DEFAULT_TEXT_STYLE, align: 'left', valign: 'top', fontSize: 24 } },
  shape: {
    shape: 'rect',
    fill: '#ffffff',
    stroke: '#1a1a2e',
    strokeWidth: 2,
    strokeStyle: 'solid',
    text: '',
    style: DEFAULT_TEXT_STYLE,
  },
  connector: {
    from: { objectId: null, anchor: 'auto', x: 0, y: 0 },
    to: { objectId: null, anchor: 'auto', x: 0, y: 0 },
    route: 'elbow',
    startCap: 'none',
    endCap: 'arrow',
    stroke: '#1a1a2e',
    strokeWidth: 2,
    strokeStyle: 'solid',
    label: '',
  },
  drawing: { points: [], stroke: '#1a1a2e', strokeWidth: 3, opacity: 1 },
  image: { src: '', name: '' },
  frame: { title: 'Frame', fill: '#ffffff' },
  card: { title: '', description: '', assignee: '', dueDate: null, color: '#6366f1' },
  table: {
    cells: [
      ['', '', ''],
      ['', '', ''],
      ['', '', ''],
    ],
    headerRow: true,
    stroke: '#9ca3af',
  },
  mindmap: {
    text: '',
    parentNode: null,
    rootId: '',
    side: 'right',
    collapsed: false,
    color: '#6366f1',
    order: 'V',
  },
  emoji: { emoji: '👍' },
};

export interface CreateContext {
  x: number;
  y: number;
  z: string;
  createdBy: string;
  id?: string;
}

/** Builds a complete object of the given type with defaults, overridden by `overrides`. */
export function createObject<T extends ObjectType>(
  type: T,
  ctx: CreateContext,
  overrides: Partial<Omit<ObjectOf<T>, 'type'>> = {},
): ObjectOf<T> {
  const size = DEFAULT_SIZES[type];
  const base: BaseObject = {
    id: ctx.id ?? newId(),
    type,
    x: ctx.x,
    y: ctx.y,
    w: size.w,
    h: size.h,
    rotation: 0,
    z: ctx.z,
    parentId: null,
    groupId: null,
    locked: false,
    createdBy: ctx.createdBy,
    link: null,
  };
  const defaults = structuredClone(TYPE_DEFAULTS[type]);
  const obj = { ...base, ...defaults, ...overrides, type } as unknown as BoardObject;
  if (obj.type === 'mindmap' && !obj.rootId) obj.rootId = obj.id;
  return obj as ObjectOf<T>;
}

export function isObjectType(value: unknown): value is ObjectType {
  return typeof value === 'string' && value in TYPE_DEFAULTS;
}

const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/**
 * Coerces `value` to the shape of `def`: wrong types fall back to the default, unknown keys are
 * dropped. Nullable fields (default null) accept null, strings and numbers; an empty default
 * array (drawing points) accepts numbers only.
 */
function sanitize(value: unknown, def: unknown): unknown {
  if (def === null)
    return value === null || typeof value === 'string' || isFiniteNumber(value) ? value : null;
  if (Array.isArray(def)) {
    if (!Array.isArray(value)) return def;
    return def.length === 0
      ? value.filter(isFiniteNumber)
      : value.map((item) => sanitize(item, def[0]));
  }
  if (typeof def === 'object') {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return def;
    const src = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const [k, d] of Object.entries(def)) out[k] = k in src ? sanitize(src[k], d) : d;
    return out;
  }
  if (typeof def === 'number') return isFiniteNumber(value) ? value : def;
  return typeof value === typeof def ? value : def;
}

/**
 * Fills missing fields of a (possibly older or partial) object with defaults and replaces fields
 * of the wrong type, so a malformed object from a peer or a file cannot break rendering.
 */
export function normalizeObject(raw: Record<string, unknown>): BoardObject | null {
  const type = raw.type;
  if (!isObjectType(type) || typeof raw.id !== 'string') return null;
  const defaults = createObject(type, { id: raw.id, x: 0, y: 0, z: 'V', createdBy: '' });
  const obj = sanitize(raw, defaults) as BoardObject;
  obj.type = type;
  if (obj.type === 'mindmap' && !obj.rootId) obj.rootId = obj.id;
  return obj;
}
